import { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { ShieldCheck, Loader2, CreditCard, ArrowLeft, CheckCircle2, AlertCircle } from "lucide-react";
import { COMPANY } from "@/lib/data";
import { fmtDate, to12h } from "@/lib/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Row = ({ label, value }) => (
  <div className="flex justify-between"><span className="text-white/45">{label}</span><span className="text-white/85">{value}</span></div>
);

export default function ReservePay() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [checking, setChecking] = useState(!!params.get("paid"));
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    axios.get(`${API}/reserve-pay/${token}`)
      .then(({ data }) => setInfo(data))
      .catch((e) => setError(e.response?.data?.detail || "This link is invalid."))
      .finally(() => setLoading(false));
  }, [token]);

  // Returning from Stripe — never trust the "?paid=1" query string itself; just poll our
  // own info endpoint until the server-verified status flips to paid.
  useEffect(() => {
    if (!checking) return;
    let tries = 0;
    const poll = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await axios.get(`${API}/reserve-pay/${token}`);
        if (data.already_paid) {
          setConfirmed(true);
          setChecking(false);
          clearInterval(poll);
        }
      } catch {
        // keep polling
      }
      if (tries >= 12) { setChecking(false); clearInterval(poll); }
    }, 1500);
    return () => clearInterval(poll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checking, token]);

  const payNow = async () => {
    setRedirecting(true);
    try {
      const { data } = await axios.post(`${API}/reserve-pay/${token}/checkout`, { origin_url: window.location.origin });
      if (data.already_paid) { setConfirmed(true); setRedirecting(false); return; }
      window.location.href = data.checkout_url;
    } catch (e) {
      setError(e.response?.data?.detail || "Could not start payment. Please try again.");
      setRedirecting(false);
    }
  };

  const paid = confirmed || info?.already_paid;

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center px-5 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-white/50 hover:text-[#F2B705] text-sm mb-6 transition-colors">
          <ArrowLeft size={16} /> Back to site
        </Link>
        <div className="flex items-center gap-3">
          <img src={COMPANY.logo} alt="Auto-Hire STL" className="h-14 w-14 object-contain rounded-md" />
          <div className="font-display font-extrabold text-2xl">AutoHire <span className="text-gold-gradient">STL</span></div>
        </div>

        <div className="mt-8 bg-[#0C0C0C] border border-white/10 rounded-2xl p-7" data-testid="reserve-pay-page">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#F2B705]" /></div>
          ) : error ? (
            <div className="text-center py-6" data-testid="reserve-pay-error">
              <AlertCircle className="text-red-400 mx-auto" size={40} />
              <p className="text-white/70 mt-3">{error}</p>
            </div>
          ) : checking ? (
            <div className="text-center py-8" data-testid="reserve-pay-checking">
              <Loader2 className="animate-spin text-[#F2B705] mx-auto" size={30} />
              <p className="text-white/60 text-sm mt-3">Confirming your payment with Stripe&hellip;</p>
            </div>
          ) : paid ? (
            <div className="text-center py-6" data-testid="reserve-pay-success">
              <CheckCircle2 className="text-emerald-400 mx-auto" size={44} />
              <h2 className="font-display font-black text-2xl mt-3">You're Confirmed!</h2>
              <p className="text-white/60 text-sm mt-2">Your $20 reservation payment was received and applied toward your rental total.</p>
              <p className="text-white/40 text-xs mt-3">We'll be in touch shortly with next steps to finish your booking.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 text-[#F2B705] text-sm font-bold uppercase tracking-widest">
                <ShieldCheck size={16} /> Secure Reservation Payment
              </div>
              <h1 className="font-display font-black text-3xl mt-2">Confirm Your Booking</h1>
              <div className="mt-5 space-y-2 text-sm">
                <Row label="Name" value={info.name} />
                <Row label="Vehicle" value={info.vehicle} />
                <Row label="Pickup" value={`${fmtDate(info.pickup_date)}${info.pickup_time ? ` at ${to12h(info.pickup_time)}` : ""} CT`} />
                <Row label="Amount Due Now" value={`$${Number(info.amount).toFixed(2)}`} />
              </div>
              <div className="mt-5 rounded-lg border border-sky-400/30 bg-sky-400/[0.06] p-3.5 text-xs text-sky-100/90 space-y-1.5">
                <p><b className="text-white">This $20 is credited in full toward your rental total</b> — it is not an additional fee.</p>
                <p>If you need to cancel, contact us before your scheduled pickup for a refund or to transfer it to a future reservation. No-call/no-shows forfeit this $20 — no additional charge is made.</p>
                <p>Already arranged to pay by cash, Cash App, or another method? Contact us directly — no need to pay here.</p>
              </div>
              <details className="mt-4 text-xs text-white/50">
                <summary className="cursor-pointer text-white/70 hover:text-[#F2B705]">Read the full Reservation Payment Policy</summary>
                <p className="mt-2 whitespace-pre-line leading-relaxed">{info.policy_text}</p>
              </details>
              <button data-testid="reserve-pay-submit" onClick={payNow} disabled={redirecting}
                className="mt-5 w-full bg-[#F2B705] text-black font-bold py-3.5 rounded-md hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
                {redirecting ? <Loader2 className="animate-spin" size={18} /> : <CreditCard size={18} />} Pay ${Number(info.amount).toFixed(2)} Securely
              </button>
              <p className="text-white/35 text-xs text-center mt-4">
                You'll be taken to Stripe's secure checkout, where you'll need to accept the{" "}
                <Link to="/reservation-policy" target="_blank" className="text-[#F2B705] hover:underline">Reservation Payment Policy</Link> before paying.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
